import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { DialogFooter, DialogClose } from '../ui/dialog';
import { CalendarIcon, Clock } from 'lucide-react';
import { heuresContratDe, semaineIsoDe, heuresPlanifiees } from './HeuresRestantes';
import { toast } from 'sonner';
import { toISODateString } from '../../lib/utils';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export function PlanningForm({ item, onClose, employes, employesDetail = [], planning = [], api, onSuccess }) {
  const [form, setForm] = useState(item ? {
    date: item.date, employe: item.employe,
    debut_midi: item.debut_midi || '', fin_midi: item.fin_midi || '',
    debut_soir: item.debut_soir || '', fin_soir: item.fin_soir || ''
  } : {
    date: toISODateString(new Date()), employe: employes[0] || '',
    debut_midi: '', fin_midi: '', debut_soir: '', fin_soir: ''
  });
  const [dateOpen, setDateOpen] = useState(false);

  // Heures restant a positionner pour l'employe choisi, sur la semaine de la date choisie,
  // en tenant compte des horaires en cours de saisie.
  const dureeSaisie = (deb, fin) => {
    if (!deb || !fin) return 0;
    const [h1, m1] = deb.split(':').map(Number); const [h2, m2] = fin.split(':').map(Number);
    const d = (h2 * 60 + m2 - (h1 * 60 + m1)) / 60;
    return d > 0 ? d : 0;
  };
  const iso = semaineIsoDe(form.date);
  const contrat = heuresContratDe(employesDetail, form.employe);
  const dejaPlanifie = iso ? heuresPlanifiees(planning, form.employe, iso.semaine, item?.id) : 0;
  const enSaisie = dureeSaisie(form.debut_midi, form.fin_midi) + dureeSaisie(form.debut_soir, form.fin_soir);
  const reste = contrat - dejaPlanifie - enSaisie;
  const fmtH = (h) => `${Math.round(h * 10) / 10}h`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (item?.id) {
        await api.put(`/planning-prev/${item.id}`, form);
        toast.success('Modifie');
      } else {
        await api.post('/planning-prev', form);
        toast.success('Cree');
      }
      onSuccess();
      onClose();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409) {
        toast.error(detail || 'Un planning existe deja pour cet employe a cette date');
      } else {
        toast.error(detail || 'Erreur');
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Date</Label>
          <Popover open={dateOpen} onOpenChange={setDateOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start text-left font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {form.date ? format(new Date(form.date), 'PPP', { locale: fr }) : 'Choisir'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar mode="single" selected={form.date ? new Date(form.date) : undefined}
                onSelect={(date) => { setForm({ ...form, date: toISODateString(date) }); setDateOpen(false); }} locale={fr} />
            </PopoverContent>
          </Popover>
        </div>
        <div className="space-y-2">
          <Label>Employe</Label>
          <Select value={form.employe} onValueChange={(v) => setForm({ ...form, employe: v })}>
            <SelectTrigger><SelectValue placeholder="Selectionner" /></SelectTrigger>
            <SelectContent>
              {employes.map(e => (<SelectItem key={e} value={e}>{e}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Service Midi</Label>
          <div className="flex gap-2">
            <Input type="time" value={form.debut_midi} onChange={(e) => setForm({ ...form, debut_midi: e.target.value })} />
            <span className="self-center text-muted-foreground">-</span>
            <Input type="time" value={form.fin_midi} onChange={(e) => setForm({ ...form, fin_midi: e.target.value })} />
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Service Soir</Label>
          <div className="flex gap-2">
            <Input type="time" value={form.debut_soir} onChange={(e) => setForm({ ...form, debut_soir: e.target.value })} />
            <span className="self-center text-muted-foreground">-</span>
            <Input type="time" value={form.fin_soir} onChange={(e) => setForm({ ...form, fin_soir: e.target.value })} />
          </div>
        </div>
      </div>
      {form.employe && iso && (
        <div className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm ${reste < -0.05 ? 'bg-red-50 border-red-300 text-red-700 dark:bg-red-950/30' : reste > 0.05 ? 'bg-amber-50 border-amber-300 text-amber-800 dark:bg-amber-950/30' : 'bg-green-50 border-green-300 text-green-700 dark:bg-green-950/30'}`} data-testid="form-heures-restantes">
          <span className="flex items-center gap-2"><Clock className="w-4 h-4" /> Semaine {iso.semaine} : {fmtH(dejaPlanifie)} deja planifiees{enSaisie > 0 ? ` + ${fmtH(enSaisie)} ici` : ''}{contrat ? ` / contrat ${fmtH(contrat)}` : ''}</span>
          <span className="font-semibold whitespace-nowrap">
            {!contrat ? 'contrat non renseigne' : reste < -0.05 ? `depasse de ${fmtH(-reste)}` : reste > 0.05 ? `reste ${fmtH(reste)}` : 'semaine complete'}
          </span>
        </div>
      )}
      <DialogFooter>
        <DialogClose asChild><Button type="button" variant="outline">Annuler</Button></DialogClose>
        <Button type="submit">{item?.id ? 'Modifier' : 'Creer'}</Button>
      </DialogFooter>
    </form>
  );
}
